import { useState, useEffect } from 'react';
import { MapPin, Truck, Navigation, ExternalLink, Copy, CheckCircle, Clock, Activity, Search, Loader2, Gauge, Compass } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getVehicleLocations } from '../../services/locationService';
import { supabase } from '../../lib/supabase';

interface TruckLocation {
  id: string;
  truckNumber: string;
  driver: string;
  currentLocation: {
    address: string;
    city: string;
    state: string;
    lat: number;
    lng: number;
  };
  status: 'moving' | 'stopped' | 'idle';
  speed: number; // mph
  heading?: number; // degrees (0-360)
  lastUpdate: string;
  loadNumber?: string;
  destination?: string;
  eta?: string;
  samsaraId?: string;
}

export function SamsaraLocationView() {
  const toast = useToast();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [trucks, setTrucks] = useState<TruckLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityFilter, setActivityFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    fetchTruckLocations();
    // Refresh every 30 seconds
    const interval = setInterval(fetchTruckLocations, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTruckLocations = async () => {
    try {
      setError(null);
      
      // Fetch vehicle locations from Samsara
      const response = await getVehicleLocations();
      
      if (!response.success || !response.locations) {
        throw new Error(response.error || 'Failed to fetch locations');
      }
      
      const locations = response.locations;
      
      // Fetch truck data from Supabase - only fleet trucks (same filter as FleetView)
      const { data: trucksData } = await supabase
        .from('trucks')
        .select('id, unit_number, samsara_id, status')
        .not('company_id', 'is', null);
      
      // Map Samsara locations to truck data
      const mappedTrucks = locations
        .map((loc: any): TruckLocation | null => {
          const truck = trucksData?.find(t => t.samsara_id === loc.vehicleId);
          if (!truck) return null;
          
          // Determine status based on speed and time
          const lastUpdateTime = new Date(loc.time);
          const now = new Date();
          const minutesAgo = Math.floor((now.getTime() - lastUpdateTime.getTime()) / 60000);
          const hoursAgo = Math.floor(minutesAgo / 60);
          
          let status: 'moving' | 'stopped' | 'idle' = 'idle';
          
          // Only consider truck active if updated within last 24 hours
          if (hoursAgo < 24) {
            if (loc.speed && loc.speed > 5) {
              status = 'moving';
            } else if (minutesAgo < 30) {
              status = 'stopped';
            }
          }
          // Otherwise, status remains 'idle' for old data
          
          // Format last update time
          let lastUpdate = '';
          if (minutesAgo < 1) {
            lastUpdate = 'Just now';
          } else if (minutesAgo < 60) {
            lastUpdate = `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
          } else if (hoursAgo < 24) {
            lastUpdate = `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
          } else {
            const daysAgo = Math.floor(hoursAgo / 24);
            lastUpdate = `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
          }
          
          // Show GPS coordinates instead of reverse geocoding (too slow for 147 trucks)
          const lat = loc.latitude || 0;
          const lng = loc.longitude || 0;
          
          return {
            id: truck.id,
            truckNumber: truck.unit_number || 'Unknown',
            driver: 'N/A', // TODO: Add driver info from dispatch
            currentLocation: {
              address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
              city: 'GPS Location',
              state: '',
              lat,
              lng,
            },
            status,
            speed: loc.speed || 0, // Already in mph from samsaraService
            heading: loc.heading,
            lastUpdate,
            samsaraId: loc.vehicleId,
          };
        })
        .filter((t: TruckLocation | null): t is TruckLocation => t !== null);
      
      setTrucks(mappedTrucks);
    } catch (err) {
      console.error('Error fetching truck locations:', err);
      setError('Failed to load truck locations');
      toast.error('Failed to load truck locations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: TruckLocation['status']) => {
    switch (status) {
      case 'moving':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'stopped':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'idle':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  const getStatusIcon = (status: TruckLocation['status']) => {
    switch (status) {
      case 'moving':
        return <Navigation className="w-4 h-4" />;
      case 'stopped':
        return <MapPin className="w-4 h-4" />;
      case 'idle':
        return <Clock className="w-4 h-4" />;
    }
  };

  const generateTrackingLink = (truckNumber: string) => {
    // Remove T- prefix for cleaner URLs
    const unitNumber = truckNumber.replace('T-', '');
    return `${window.location.origin}/track/${unitNumber}`;
  };

  const handleCopyLink = (truckNumber: string) => {
    const link = generateTrackingLink(truckNumber);
    navigator.clipboard.writeText(link);
    setCopiedId(truckNumber);
    toast.success('Tracking link copied to clipboard!');
    
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getDirectionFromHeading = (heading: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(heading / 45) % 8;
    return directions[index];
  };

  const handleOpenMap = (truck: TruckLocation) => {
    // Open tracking page for this truck - use unit_number without T- prefix
    const unitNumber = truck.truckNumber.replace('T-', '');
    const url = `${window.location.origin}/track/${unitNumber}`;
    window.open(url, '_blank');
  };

  // Helper function to check if truck is recently active (updated within 24 hours)
  const isRecentlyActive = (truck: TruckLocation) => {
    const updateText = truck.lastUpdate.toLowerCase();
    if (updateText.includes('just now') || updateText.includes('minute') || updateText.includes('hour')) {
      return true;
    }
    if (updateText.includes('day')) {
      const days = parseInt(updateText);
      return days === 1;
    }
    return false;
  };

  const filteredTrucks = trucks.filter(truck => {
    // Apply search filter
    const matchesSearch = 
      truck.truckNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.driver.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.loadNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.currentLocation.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.currentLocation.state.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // Apply activity filter
    if (activityFilter === 'active') {
      return isRecentlyActive(truck);
    } else if (activityFilter === 'inactive') {
      return !isRecentlyActive(truck);
    }
    
    return true; // 'all' filter
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Samsara Live Tracking</h2>
            <p className="text-blue-100">
              Real-time truck locations and shareable tracking links for brokers
            </p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            {loading ? (
              <Loader2 className="w-12 h-12 animate-spin" />
            ) : (
              <MapPin className="w-12 h-12" />
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by truck, driver, load, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#AC021A] focus:border-[#AC021A]"
          />
        </div>

        {/* Activity Filter Buttons */}
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => setActivityFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              activityFilter === 'all'
                ? 'bg-white text-[#AC021A] shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All ({trucks.length})
          </button>
          <button
            onClick={() => setActivityFilter('active')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              activityFilter === 'active'
                ? 'bg-white text-green-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Active ({trucks.filter(t => isRecentlyActive(t)).length})
          </button>
          <button
            onClick={() => setActivityFilter('inactive')}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              activityFilter === 'inactive'
                ? 'bg-white text-slate-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Inactive ({trucks.filter(t => !isRecentlyActive(t)).length})
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
            <Activity className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-green-900">{filteredTrucks.filter(t => t.status === 'moving').length} Moving</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <MapPin className="w-4 h-4 text-red-600" />
            <span className="text-sm font-semibold text-red-900">{filteredTrucks.filter(t => t.status === 'stopped').length} Stopped</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Clock className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-900">{filteredTrucks.filter(t => t.status === 'idle').length} Idle</span>
          </div>
        </div>
      </div>

      {/* All Trucks Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900">All Trucks ({filteredTrucks.length})</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Truck</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Speed</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Direction</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Current Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Updated</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTrucks.map((truck) => (
                <tr key={truck.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-[#AC021A] p-1.5 rounded">
                        <Truck className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-semibold text-slate-900">{truck.truckNumber}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(truck.status)}`}>
                      {getStatusIcon(truck.status)}
                      {truck.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Gauge className="w-4 h-4 text-slate-400" />
                      <span className={`text-sm font-semibold ${
                        truck.speed > 60 ? 'text-green-600' :
                        truck.speed > 30 ? 'text-blue-600' :
                        truck.speed > 0 ? 'text-yellow-600' :
                        'text-slate-400'
                      }`}>
                        {truck.speed > 0 ? `${truck.speed} mph` : 'Stopped'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {truck.heading !== undefined ? (
                      <div className="flex items-center gap-1.5">
                        <Compass 
                          className="w-4 h-4 text-slate-400" 
                          style={{ transform: `rotate(${truck.heading}deg)` }}
                        />
                        <span className="text-sm text-slate-600">
                          {getDirectionFromHeading(truck.heading)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm text-slate-900">{truck.currentLocation.city}</p>
                      <p className="text-xs text-slate-500">{truck.currentLocation.address}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-500">{truck.lastUpdate}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleOpenMap(truck)}
                        className="p-2 text-[#AC021A] hover:bg-red-50 rounded-lg transition-colors"
                        title="View on Map"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCopyLink(truck.truckNumber)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Copy Tracking Link"
                      >
                        {copiedId === truck.truckNumber ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">📍 About Samsara Tracking</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Real-time GPS location</strong> updates from Samsara devices every 30 seconds</li>
          <li>• <strong>Speed monitoring</strong> with color-coded indicators (Green: 60+ mph, Blue: 30-60 mph, Yellow: 1-30 mph)</li>
          <li>• <strong>Direction/Heading</strong> showing compass direction (N, NE, E, SE, S, SW, W, NW)</li>
          <li>• <strong>Status tracking</strong>: Moving (green), Stopped (red), or Idle (yellow)</li>
          <li>• <strong>Share tracking links</strong> with brokers for load visibility</li>
          <li>• Click "View on Map" to see exact location with full details</li>
        </ul>
      </div>
    </div>
  );
}
